import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Release, Project, Change } from '../../models';
import { Timeline, Pagination, Card } from 'flowbite-react';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { Loader } from '../Loader';
import { CopyButton } from '../CopyButton';
import { Tooltip } from '../Tooltip';
import { ChangeModal } from '../ChangeModal';
import { RootState, useAppDispatch, useAppSelector } from '../../redux';
import { loadReleases, getChangelog, loadChange, resetChanges, updateChange, reorderReleaseChanges } from '../../redux';
import ChangelogImages from './ChangelogImages';
import { formatMomentDate } from '../../locales';
import {
    handleChangeMiddleClick,
    preventMiddleClickAutoscroll,
} from '../../utils/openChangeInNewBrowserTab';

interface ChangelogProps {
    releases?: Release[];
    upcoming?: Change[];
    project?: Project;
    page?: number,
    nbPerPage?: number,
    isPublic?: boolean,
}

interface ChangelogEntryProps {
    change: Change;
    isPublic?: boolean;
    canMoveUp?: boolean;
    canMoveDown?: boolean;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    onEdit?: () => void;
    onIgnore?: () => void;
}

function ChangelogEntry(props: ChangelogEntryProps) {
    const { change, isPublic, canMoveUp, canMoveDown, onMoveUp, onMoveDown, onEdit, onIgnore } = props;
    const { t, i18n } = useTranslation();
    const editable = !isPublic && !!(onEdit || onIgnore || onMoveUp || onMoveDown);

    return (
        <div className="group/entry relative pr-8">
            <span className="text-gray-900 dark:text-gray-100">- {change.localizedReleaseTitle(i18n.language)}</span>
            <p className="text-sm font-light mb-2 [&_img]:max-h-80 [&_img]:w-auto [&_img]:max-w-full [&_img]:object-contain [&_img]:rounded [&_img]:border">
                {change.localizedReleaseDescription(i18n.language)?.split('\n').map((item, key) => (
                    <span key={key} dangerouslySetInnerHTML={{ __html: item }} />
                ))}
            </p>
            <ChangelogImages urls={change.getReleaseImageUrls()} alt={change.localizedReleaseTitle(i18n.language) || ''} />
            {editable && (
                <div className="absolute right-0 top-0 flex items-center gap-0.5 rounded bg-white/90 dark:bg-gray-800/95 opacity-0 pointer-events-none transition-opacity group-hover/entry:opacity-100 group-hover/entry:pointer-events-auto group-focus-within/entry:opacity-100 group-focus-within/entry:pointer-events-auto max-md:opacity-100 max-md:pointer-events-auto">
                    {onMoveUp && (
                        <Tooltip content={t('projects.move_change_up')}>
                            <button
                                type="button"
                                disabled={!canMoveUp}
                                title={t('projects.move_change_up') || ''}
                                className="p-0.5 rounded text-gray-400 hover:text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800 disabled:opacity-20 disabled:hover:bg-transparent"
                                onClick={onMoveUp}
                            >
                                <Icon type="up" size={4} color="gray-500" />
                            </button>
                        </Tooltip>
                    )}
                    {onMoveDown && (
                        <Tooltip content={t('projects.move_change_down')}>
                            <button
                                type="button"
                                disabled={!canMoveDown}
                                title={t('projects.move_change_down') || ''}
                                className="p-0.5 rounded text-gray-400 hover:text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800 disabled:opacity-20 disabled:hover:bg-transparent"
                                onClick={onMoveDown}
                            >
                                <Icon type="down" size={4} color="gray-500" />
                            </button>
                        </Tooltip>
                    )}
                    {onEdit && (
                        <Tooltip content={t('common.edit')}>
                            <button
                                type="button"
                                title={t('common.edit') || ''}
                                className="p-0.5 rounded text-gray-400 hover:text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800"
                                onClick={onEdit}
                                onMouseDown={preventMiddleClickAutoscroll}
                                onAuxClick={(event) => handleChangeMiddleClick(event, change)}
                            >
                                <Icon type="edit" size={4} color="gray-500" />
                            </button>
                        </Tooltip>
                    )}
                    {onIgnore && (
                        <Tooltip content={t('projects.ignore_from_changelog')}>
                            <button
                                type="button"
                                title={t('projects.ignore_from_changelog') || ''}
                                className="p-0.5 rounded text-gray-400 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:bg-gray-800"
                                onClick={onIgnore}
                            >
                                <Icon type="close" size={4} color="gray-500" />
                            </button>
                        </Tooltip>
                    )}
                </div>
            )}
        </div>
    );
}

export interface ChangelogUpcomingProps {
    upcoming: Change[];
    orderedTypesToShow: string[];
    isPublic?: boolean;
    onEdit?: (change: Change) => void;
    onIgnore?: (change: Change) => void;
}

export function ChangelogUpcoming(props: ChangelogUpcomingProps) {
    const { upcoming, orderedTypesToShow, isPublic, onEdit, onIgnore } = props;
    const { t } = useTranslation();
    const [visibleUpcoming, setVisibleUpcoming] = useState(true);

    if (!upcoming?.length)
        return null;

    const types = orderedTypesToShow.filter(type =>
        upcoming.some((change: Change) => change.type === type && !change.omitFromChangelog)
    );
    if (!types.length)
        return null;

    return (
        <div>
            <Button
                type="button"
                icon={visibleUpcoming ? 'close' : 'down'}
                className="sm"
                title={ t(visibleUpcoming ? 'projects.hide_upcoming' : 'projects.show_upcoming') }
                color="default"
                onClick={() => setVisibleUpcoming(!visibleUpcoming)}
            />
            {visibleUpcoming && (
                <div className="mt-4 border p-4 bg-gray-100 dark:bg-gray-800 rounded">
                    <h3 className="text-lg font-medium leading-7 flex items-center text-gray-900 dark:text-gray-100 sm:truncate sm:text-xl sm:tracking-tight italic">
                        <Icon type="timer" size={7} className="mr-1.5" />
                        {t('projects.upcoming_release')}
                    </h3>
                    {types.map(type => (
                        <div key={type} className="flex flex-col space-y-2 mt-1">
                            <h3 className="text-black dark:text-gray-100 text-lg underline font-bold">
                                <Icon type={Change.iconName(type)} size={5} className="inline mr-1.5" />
                                {t(`projects.${type}_plural`)}
                            </h3>
                            {upcoming.filter((change: Change) =>
                                change.type === type && !change.omitFromChangelog
                            ).map((change: Change) => (
                                <ChangelogEntry
                                    key={change._id}
                                    change={change}
                                    isPublic={isPublic}
                                    onEdit={onEdit ? () => onEdit(change) : undefined}
                                    onIgnore={onIgnore ? () => onIgnore(change) : undefined}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Changelog(props: ChangelogProps) {
    const { page, nbPerPage, isPublic } = props;

    const { t }   = useTranslation();
    const storeReleases = useAppSelector((state: RootState) => state.releases.releases);
    const { loading }   = useAppSelector((state: RootState) => state.releases);
    const changelogState = useAppSelector((state: RootState) => state.projects.changelog);
    const { change } = useAppSelector((state: RootState) => state.changes);

    const [filtered, setFiltered]               = useState([]);
    const [visible, setVisible]                 = useState([]);
    const [currentPage, setCurrentPage]         = useState(page || 1);
    const [currentChangeId, setCurrentChangeId] = useState<string | undefined>();

    const pageLength = nbPerPage || 10;

    const releases = React.useMemo(() => {
        if (props.releases)
            return props.releases;
        return storeReleases;
    }, [props.releases, storeReleases]);

    const upcoming = React.useMemo(() => {
        if (props.upcoming)
            return props.upcoming;
        if (props.project && changelogState?.project?._id === props.project._id)
            return changelogState.upcoming || [];
        return [];
    }, [props.upcoming, props.project, changelogState]);

    const orderedTypesToShow = React.useMemo(() => {
        const types = [
            Change.TYPE_FEATURE,
            Change.TYPE_FIX,
            Change.TYPE_UI,
            Change.TYPE_I18N,
            Change.TYPE_DOC,
            Change.TYPE_TEST,
            Change.TYPE_BUILD,
            Change.TYPE_REFACTOR,
        ];
        if (!isPublic) {
            types.push(Change.TYPE_LINT);
            types.push(Change.TYPE_CHORE);
            types.push(Change.TYPE_DEPS);
        }
        return types;
    }, [isPublic]);

    const dispatch = useAppDispatch();
    useEffect(() => {
        if (props.project)
            dispatch(loadReleases({project: props.project._id}));
    }, [dispatch, props.project]);

    useEffect(() => {
        if (props.project && !isPublic)
            dispatch(getChangelog(props.project._id));
    }, [dispatch, props.project, isPublic]);

    useEffect(() => {
        setCurrentPage(1);
        let list = (releases || []) as Release[];
        if (isPublic) {
            // Hide releases with no client-visible entries (e.g. only ignored / empty).
            list = list.filter((release: Release) =>
                orderedTypesToShow.some(type => release.hasChangesOfType(type))
            );
        }
        setFiltered(list as never[]);
    }, [releases, isPublic, orderedTypesToShow]);

    useEffect(() => {
        setVisible(filtered?.slice((currentPage -1) * pageLength, currentPage * pageLength) as never[]);
    }, [releases, currentPage, filtered, pageLength]);

    const totalPages = React.useMemo(() => {
        let totalPages = 1;
        if (filtered?.length > 0) {
            totalPages = filtered?.length / pageLength;
            if ((filtered?.length % pageLength) > 0)
                totalPages = parseInt(totalPages + "") + 1;
        }
        return totalPages;
    }, [filtered, pageLength]);

    const publicUrl = React.useMemo(() => {
        if (isPublic || !props.project)
            return null;
        return window.location.origin + Project.publicChangelogPath(props.project);
    }, [isPublic, props.project]);

    const selectChange = (c?: Change) => {
        setCurrentChangeId(c?._id);
        if (c)
            dispatch(loadChange(c._id));
        else
            dispatch(resetChanges(/* onlyItem */ true));
    };

    const ignoreChange = (c: Change) => {
        dispatch(updateChange({ _id: c._id, omitFromChangelog: true }, true, true));
    };

    const moveChange = (release: Release, c: Change, direction: -1 | 1) => {
        const ofType = release.changes.filter((item: Change) =>
            item.type === c.type && !item.omitFromChangelog
        );
        const idx = ofType.findIndex((item: Change) => item._id === c._id);
        const other = ofType[idx + direction];
        if (!other)
            return;
        const ids = release.changes.map((item: Change) => item._id);
        const a = ids.indexOf(c._id);
        const b = ids.indexOf(other._id);
        const next = [...ids];
        next[a] = ids[b];
        next[b] = ids[a];
        dispatch(reorderReleaseChanges(release._id, next));
    };

    const releaseSummary = (release: Release) => {
        if (!release)
            return null;

        return orderedTypesToShow.filter(type => release.hasChangesOfType(type)).map(type => {
            const ofType = release.changesOfType(type);
            return (
                <div key={type} className="flex flex-col space-y-2 mt-1">
                    <h3 className="text-black dark:text-gray-100 text-lg underline font-bold">
                        <Icon type={Change.iconName(type)} size={5} className="inline mr-1.5" />
                        {t(`projects.${type}_plural`)}
                    </h3>
                    {ofType.map((c: Change, i: number) => (
                        <ChangelogEntry
                            key={c._id}
                            change={c}
                            isPublic={isPublic}
                            canMoveUp={i > 0}
                            canMoveDown={i < ofType.length - 1}
                            onMoveUp={() => moveChange(release, c, -1)}
                            onMoveDown={() => moveChange(release, c, 1)}
                            onEdit={() => selectChange(c)}
                            onIgnore={() => ignoreChange(c)}
                        />
                    ))}
                </div>
            );
        });
    };

    if (loading === 'pending')
        return <Loader />;

    return (
        <Card className="Changelog overflow-visible">
            <div className="flex items-center justify-between gap-3">
                <h2 className="text-2xl font-bold leading-7 flex items-center text-gray-900 dark:text-gray-100 sm:truncate sm:text-3xl sm:tracking-tight">
                    <Icon type="changelog" size={7} className="mr-1.5" />
                    {t('projects.changelog')}
                </h2>
                {publicUrl && (
                    <div className="flex items-center gap-2 shrink-0 font-normal">
                        <CopyButton
                            textToCopy={publicUrl}
                            title={t('projects.public_changelog_link') || ''}
                            hover
                            size={4}
                            className="text-sm text-gray-500 dark:text-gray-400"
                        />
                        <a
                            href={publicUrl}
                            target="_blank"
                            rel="noreferrer"
                            title={t('projects.open_public_changelog') || ''}
                            className="text-gray-400 hover:text-gray-700 dark:text-gray-200"
                        >
                            <Icon type="eye" size={5} color="gray-500" />
                        </a>
                    </div>
                )}
            </div>
            <ChangelogUpcoming
                upcoming={upcoming}
                orderedTypesToShow={orderedTypesToShow}
                isPublic={isPublic}
                onEdit={selectChange}
                onIgnore={ignoreChange}
            />
            {(releases.length > 0) && (
                <>
                    <Timeline>
                        {visible?.map((release: Release) => (
                        <Timeline.Item key={release._id} className="mb-5">
                            <Timeline.Point icon={() => <div className="w-fit mx-auto"><Icon type="release" size={3} /></div>}/>
                            <Timeline.Content>
                                <Timeline.Title className="truncate">
                                    <span>
                                        {release.version}
                                    </span>
                                </Timeline.Title>
                                <Timeline.Time>
                                    {formatMomentDate(release.date, 'LLL')}
                                </Timeline.Time>
                                <Timeline.Body>
                                    {releaseSummary(release)}
                                </Timeline.Body>
                            </Timeline.Content>
                        </Timeline.Item>
                        ))}
                    </Timeline>
                    <div className="flex items-center justify-center text-center">
                        <Pagination
                            currentPage={currentPage}
                            onPageChange={n => setCurrentPage(n)}
                            showIcons
                            totalPages={totalPages}
                            previousLabel=""
                            nextLabel=""
                        />
                    </div>
                </>
            )}
            {!isPublic && change && change._id === currentChangeId && change.project?.name && (
                <ChangeModal
                    project={change.project}
                    change={change}
                    show={true}
                    showProjectName={true}
                    onClose={() => selectChange()}
                    onRemove={() => { }}
                />
            )}
        </Card>
    );
}

export default Changelog;
